import React, { useEffect, useMemo, useState } from 'react';
import Icons from '../Icons';

const normalize = (value) => (value || '').toString().toLowerCase();

export default function SearchableSelect({
  id,
  value,
  options = [],
  onChange,
  placeholder = 'Search...',
  emptyLabel = 'No results found.',
  maxVisible = 10,
  disabled = false,
  className = '',
  listClassName = '',
  ariaLabel,
  allowCustomValue = false
}) {
  const [query, setQuery] = useState('');
  const listId = `${id || 'search-select'}-listbox`;
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  useEffect(() => {
    if (selectedOption && value) {
      setQuery(selectedOption.label);
      return;
    }
    if (allowCustomValue && value) {
      setQuery(value.toString());
      return;
    }
    if (!value) {
      setQuery('');
    }
  }, [allowCustomValue, selectedOption, value]);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const needle = normalize(query);
    return options.filter((option) => {
      const label = normalize(option.label);
      const meta = normalize(option.meta);
      const searchText = normalize(option.searchText);
      return label.includes(needle) || meta.includes(needle) || searchText.includes(needle);
    });
  }, [options, query]);

  const maxHeight = maxVisible ? `${maxVisible * 36}px` : undefined;

  return (
    <div className={`sp-search-select ${className}`.trim()}>
      <div className={`sp-search sp-search-select__input ${disabled ? 'is-disabled' : ''}`.trim()}>
        <Icons.Search />
        <input
          id={id}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          value={query}
          disabled={disabled}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel || placeholder}
        />
        {query && !disabled && (
          <button
            type="button"
            className="sp-clear sp-clear--muted"
            aria-label="Clear search"
            onClick={() => setQuery('')}
          >
            <Icons.X />
          </button>
        )}
      </div>
      <div
        id={listId}
        className={`sp-search-list__items ${listClassName}`.trim()}
        role="listbox"
        aria-label={ariaLabel || 'Search results'}
        style={{ maxHeight }}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`sp-search-option ${isSelected ? 'is-selected' : ''}`.trim()}
                onClick={() => onChange(option.value)}
                disabled={disabled}
              >
                <span>{option.label}</span>
                {option.meta && <span className="sp-search-option__meta">{option.meta}</span>}
                {isSelected && (
                  <span className="sp-search-option__icon" aria-hidden="true">
                    <Icons.Check />
                  </span>
                )}
              </button>
            );
          })
        ) : (
          <div className="sp-search-empty">{emptyLabel}</div>
        )}
      </div>
      <div className="text-xs sp-muted" aria-live="polite">
        {filteredOptions.length} result{filteredOptions.length === 1 ? '' : 's'}
      </div>
    </div>
  );
}
